import * as _ from "lodash";
import type { ReactNode, Ref } from "react";
import { use, useEffect, useImperativeHandle, useState } from "react";

import FormContext from "./FormContext";

interface FormControlProps {
    children?: ReactNode;
    className?: string;
    required?: boolean;
    name?: string;
    condition?: (value: unknown) => boolean;
    ref?: Ref<unknown>;
}

/**
 * Wraps a single form field and registers its validator with the parent form.
 *
 * @param root0 - The component props. `ref` exposes `validate()` and `name`.
 * @returns The rendered form control.
 */
const FormControl = ({
    children,
    className = "",
    required,
    name,
    condition = (value) => !!value && (!_.isNumber(value) || value > 0),
    ref,
}: FormControlProps) => {
    const [valid, setValid] = useState(true);
    const formContext = use(FormContext);

    const validate = (value) => {
        const validated = !required ? true : condition(value);
        if (valid !== !!validated) {
            setValid(!!validated);
        }
        return validated;
    };

    useImperativeHandle(ref, () => ({
        validate,
        name,
    }));

    useEffect(() => {
        formContext?.addControl(name, { validate, name });
    });

    return (
        <div
            className={`lights-map-form-control ${className} ${!valid ? "invalid" : ""}`}
        >
            {children}
        </div>
    );
};

export default FormControl;
